import { apiClient } from './client';
import { API_CONFIG } from '../../config/api';
import type {
  Instrument,
  InstrumentsResponse,
  InstrumentPrice,
  InstrumentFilters,
  PriceUpdate,
  MarketInstrument,
  AssetClass
} from '../../types/instrument';
import { MARKET_TYPE_MAP } from '../../types/instrument';

/**
 * Instrument Service - Handles all instrument-related API operations
 * Based on Trading API v1.1.0 specification
 */
export class InstrumentService {
  /**
   * Fetch all available instruments
   * GET /v1/instruments
   */
  async getInstruments(filters?: InstrumentFilters): Promise<InstrumentsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.product) params.append('product', filters.product);
    if (filters?.assetClass) params.append('assetClass', filters.assetClass);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const endpoint = params.toString() ? `/v1/instruments?${params.toString()}` : '/v1/instruments';
    return apiClient.get<InstrumentsResponse>(endpoint);
  }

  /**
   * Fetch detailed information for a specific instrument
   * GET /v1/instruments/{instrument_id}
   */
  async getInstrumentDetails(instrumentId: string): Promise<Instrument> {
    return apiClient.get<Instrument>(`/v1/instruments/${instrumentId}`);
  }

  /**
   * Fetch current price for a specific instrument
   * GET /v1/instruments/{instrument_id}/price
   */
  async getInstrumentPrice(instrumentId: string): Promise<InstrumentPrice> {
    return apiClient.get<InstrumentPrice>(`/v1/instruments/${instrumentId}/price`);
  }

  /**
   * Fetch current prices for multiple instruments
   * GET /v1/instruments/prices
   */
  async getMultipleInstrumentPrices(instrumentIds: string[]): Promise<{ prices: InstrumentPrice[] }> {
    const params = new URLSearchParams();
    params.append('ids', instrumentIds.join(','));
    
    return apiClient.get<{ prices: InstrumentPrice[] }>(`/v1/instruments/prices?${params.toString()}`);
  }

  /**
   * Get instruments by asset class with prices
   */
  async getInstrumentsByAssetClass(assetClass: AssetClass): Promise<MarketInstrument[]> {
    try {
      // Fetch instruments for the asset class
      const response = await this.getInstruments({ 
        assetClass, 
        status: 'active',
        limit: 50 
      });

      // Get instrument IDs for price fetching
      const instrumentIds = response.instruments.map(inst => inst.id);
      
      if (instrumentIds.length === 0) {
        return [];
      }

      // Fetch current prices for all instruments
      const pricesResponse = await this.getMultipleInstrumentPrices(instrumentIds);
      const pricesMap = new Map(
        pricesResponse.prices.map(price => [price.instrumentId, price])
      );

      // Convert to MarketInstrument format
      return response.instruments.map(instrument => {
        const price = pricesMap.get(instrument.id);
        const currentPrice = price || instrument.currentPrice;
        const change = instrument.priceHistory?.daily?.changePercent || 0;
        
        return {
          market: MARKET_TYPE_MAP[instrument.assetClass],
          symbol: instrument.symbol,
          name: instrument.name,
          price: currentPrice ? this.formatPrice(currentPrice.bid || currentPrice.ask || 0) : '--',
          change: this.formatChange(change),
          instrumentId: instrument.id,
          assetClass: instrument.assetClass,
          bid: currentPrice?.bid || 0,
          ask: currentPrice?.ask || 0,
        };
      });
    } catch (error) {
      console.error(`Failed to fetch instruments for ${assetClass}:`, error);
      return [];
    }
  }

  /**
   * Get available market types based on instruments that exist in the system
   */
  async getAvailableMarketTypes(): Promise<string[]> {
    try {
      // Fetch a sample of instruments to determine available asset classes
      const response = await this.getInstruments({ 
        status: 'active',
        limit: 1000 // Get a large sample to ensure we capture all asset classes
      });

      // Extract unique asset classes from instruments
      const uniqueAssetClasses = new Set<AssetClass>();
      response.instruments.forEach(instrument => {
        uniqueAssetClasses.add(instrument.assetClass);
      });

      // Convert asset classes to market types and sort them
      const marketTypes = Array.from(uniqueAssetClasses)
        .map(assetClass => MARKET_TYPE_MAP[assetClass])
        .filter(Boolean) // Remove any undefined values
        .sort();

      return marketTypes;
    } catch (error) {
      console.error('Failed to fetch available market types:', error);
      // Return default market types as fallback
      return ['Forex', 'Indices', 'Crypto', 'Commodities', 'Synthetic'];
    }
  }

  /**
   * Search instruments by symbol or name
   */
  async searchInstruments(query: string, assetClass?: AssetClass): Promise<MarketInstrument[]> {
    try {
      // For now, we'll fetch all instruments and filter client-side
      // In a real implementation, the API might support search parameters
      const response = await this.getInstruments({ 
        assetClass,
        status: 'active',
        limit: 100 
      });

      const filteredInstruments = response.instruments.filter(instrument =>
        instrument.symbol.toLowerCase().includes(query.toLowerCase()) ||
        instrument.name.toLowerCase().includes(query.toLowerCase())
      );

      // Get prices for filtered instruments
      if (filteredInstruments.length === 0) {
        return [];
      }

      const instrumentIds = filteredInstruments.map(inst => inst.id);
      const pricesResponse = await this.getMultipleInstrumentPrices(instrumentIds);
      const pricesMap = new Map(
        pricesResponse.prices.map(price => [price.instrumentId, price])
      );

      return filteredInstruments.map(instrument => {
        const price = pricesMap.get(instrument.id);
        const currentPrice = price || instrument.currentPrice;
        const change = instrument.priceHistory?.daily?.changePercent || 0;
        
        return {
          market: MARKET_TYPE_MAP[instrument.assetClass],
          symbol: instrument.symbol,
          name: instrument.name,
          price: currentPrice ? this.formatPrice(currentPrice.bid || currentPrice.ask || 0) : '--',
          change: this.formatChange(change),
          instrumentId: instrument.id,
          assetClass: instrument.assetClass,
          bid: currentPrice?.bid || 0,
          ask: currentPrice?.ask || 0,
        };
      });
    } catch (error) {
      console.error('Failed to search instruments:', error);
      return [];
    }
  }

  /**
   * Format price for display
   */
  private formatPrice(price: number): string {
    if (price === 0) return '--';
    
    // Format based on price magnitude
    if (price >= 1000) {
      return price.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    } else if (price >= 1) {
      return price.toFixed(4);
    } else {
      return price.toFixed(6);
    }
  }

  /**
   * Format change percentage for display
   */
  private formatChange(changePercent: number): string {
    if (changePercent === 0) return '0.00%';
    
    const formatted = Math.abs(changePercent).toFixed(2);
    return changePercent >= 0 ? `+${formatted}%` : `-${formatted}%`;
  }
}

/**
 * Real-time Price Streaming Service using fetch() with ReadableStream for SSE with headers
 */
export class PriceStreamService {
  private abortController: AbortController | null = null;
  private subscribers = new Map<string, (update: PriceUpdate) => void>();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private isConnected = false;

  /**
   * Start price streaming for specified instruments using fetch with custom headers
   */
  startPriceStream(instrumentIds: string[], onPriceUpdate: (update: PriceUpdate) => void): void {
    if (this.abortController) {
      this.stopPriceStream();
    }

    this.abortController = new AbortController();
    const streamUrl = this.buildStreamUrl(instrumentIds);
    
    this.connectSSE(streamUrl, onPriceUpdate, instrumentIds);
  }

  /**
   * Connect to SSE stream using fetch() with Authorization header
   */
  private async connectSSE(
    url: string, 
    onPriceUpdate: (update: PriceUpdate) => void,
    instrumentIds: string[]
  ): Promise<void> {
    try {
      const token = apiClient.getToken();
      
      if (!token) {
        console.error('No authentication token available for SSE connection');
        return;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/event-stream',
          'Cache-Control': 'no-cache',
          'Authorization': `Bearer ${token}`,
        },
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status} ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      this.isConnected = true;
      this.reconnectAttempts = 0;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      this.readSSEStream(reader, decoder, onPriceUpdate, instrumentIds);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      console.error('SSE connection error:', error);
      this.isConnected = false;
      this.handleReconnect(instrumentIds, onPriceUpdate);
    }
  }

  /**
   * Read SSE stream from fetch response
   */
  private async readSSEStream(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    decoder: TextDecoder,
    onPriceUpdate: (update: PriceUpdate) => void,
    instrumentIds: string[]
  ): Promise<void> {
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          this.parseSSEEvent(line.trim(), onPriceUpdate);
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      
      console.error('Error reading SSE stream:', error);
      this.isConnected = false;
      this.handleReconnect(instrumentIds, onPriceUpdate);
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Parse SSE event line
   */
  private parseSSEEvent(line: string, onPriceUpdate: (update: PriceUpdate) => void): void {
    if (line.startsWith('data: ')) {
      try {
        const data = line.substring(6); // Remove 'data: ' prefix
        const eventData = JSON.parse(data);
        
        // Check if this is a price event
        if (eventData.instrumentId && eventData.bid !== undefined && eventData.ask !== undefined) {
          const priceUpdate: PriceUpdate = {
            instrumentId: eventData.instrumentId,
            bid: eventData.bid,
            ask: eventData.ask,
            timestamp: eventData.timestamp || new Date().toISOString()
          };
          
          onPriceUpdate(priceUpdate);
          
          // Notify instrument-specific subscribers
          const callback = this.subscribers.get(priceUpdate.instrumentId);
          if (callback) {
            callback(priceUpdate);
          }
        }
      } catch (error) {
        console.error('Failed to parse SSE data:', error);
      }
    } else if (line.startsWith('event: ')) {
      // Handle different event types if needed
      // const eventType = line.substring(7); // Event type not currently used
    }
  }

  /**
   * Subscribe to price updates for a specific instrument
   */
  subscribeToPriceUpdates(instrumentId: string, callback: (update: PriceUpdate) => void): void {
    this.subscribers.set(instrumentId, callback);
  }

  /**
   * Unsubscribe from price updates for a specific instrument
   */
  unsubscribeFromPriceUpdates(instrumentId: string): void {
    this.subscribers.delete(instrumentId);
  }

  /**
   * Stop price streaming
   */
  stopPriceStream(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.subscribers.clear();
    this.reconnectAttempts = 0;
    this.isConnected = false;
  }

  /**
   * Check if currently connected
   */
  isStreamConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Build SSE stream URL
   */
  private buildStreamUrl(instrumentIds: string[]): string {
    const baseUrl = `${API_CONFIG.BASE_URL}/v1/stream/connect`; // Use environment-aware API config
    const params = new URLSearchParams();
    
    params.append('channels', 'prices');
    params.append('instruments', instrumentIds.join(','));
    
    return `${baseUrl}?${params.toString()}`;
  }

  /**
   * Handle reconnection with exponential backoff
   */
  private handleReconnect(instrumentIds: string[], onPriceUpdate: (update: PriceUpdate) => void): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    setTimeout(() => {
      this.startPriceStream(instrumentIds, onPriceUpdate);
    }, delay);
  }
}

// Export singleton instances
export const instrumentService = new InstrumentService();
export const priceStreamService = new PriceStreamService();
